import { useCallback, useEffect, useState } from 'react';
import { LifeBuoy, MessageCircle, Search } from 'lucide-react';
import { toast } from 'sonner';
import { api, apiError } from '@/lib/api';
import { LoadingState, EmptyState } from '@/components/states';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export const SUPPORT_LABELS = { NEW: 'Nouvelle', IN_PROGRESS: 'En cours', RESOLVED: 'Traitée', CLOSED: 'Clôturée' };
const COLORS = { NEW: 'bg-brand/15 text-brand', IN_PROGRESS: 'bg-[#F59E0B]/15 text-[#F59E0B]', RESOLVED: 'bg-[#22C55E]/15 text-[#22C55E]', CLOSED: 'bg-white/10 text-muted-foreground' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
export const WA_MESSAGE = 'Vérification de compte terminé by MyMusic Ia.\n\nCliquez ici pour obtenir vos crédits et commencer à créer vos musiques personnalisées sur MyMusic IA : https://mymusicia.com/credits';
const waDigits = (n) => { const d = (n || '').replace(/\D/g, ''); return d.length >= 8 && d.length <= 15 ? d : null; };
export const waLink = (n) => { const d = waDigits(n); return d ? `https://wa.me/${d}?text=${encodeURIComponent(WA_MESSAGE)}` : null; };

function Badge({ s }) {
  return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${COLORS[s] || ''}`}>{SUPPORT_LABELS[s] || s}</span>;
}

function WhatsAppButton({ number, testId }) {
  const href = waLink(number);
  if (!href) {
    return (
      <span data-testid={`${testId}-invalid`} title="Numéro WhatsApp absent ou invalide" onClick={(e) => e.stopPropagation()}
            className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-muted-foreground">
        <MessageCircle className="h-3.5 w-3.5" /> Numéro invalide
      </span>
    );
  }
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testId} onClick={(e) => e.stopPropagation()}
       className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-3 py-1.5 text-xs font-semibold text-black hover:brightness-110">
      <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
    </a>
  );
}

function Detail({ req, onClose, onSaved }) {
  const [status, setStatus] = useState(req.status);
  const [note, setNote] = useState(req.admin_note || '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/admin/support-requests/${req.id}`, { status, admin_note: note }); toast.success('Demande mise à jour'); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const row = (k, v) => <div className="flex justify-between gap-4 border-b border-border py-2 text-sm"><span className="text-muted-foreground">{k}</span><span className="break-all text-right text-white">{v || '—'}</span></div>;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass max-w-lg" data-testid="support-detail-dialog">
        <DialogHeader><DialogTitle>Demande d'assistance crédits</DialogTitle></DialogHeader>
        <div>
          {row('Identifiant', <span className="font-mono text-xs">{req.id}</span>)}
          {row('Utilisateur', <span className="font-mono text-xs">{req.user_id}</span>)}
          {row('Nom / pseudo', `${req.display_name || ''}${req.username ? ` (@${req.username})` : ''}`)}
          {row('WhatsApp', <span className="font-mono">{req.whatsapp_number}</span>)}
          {row('Date', fmt(req.created_at))}
          {row('Source', req.source)}
          {req.message && row('Message', req.message)}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44 border-border bg-surface-input" data-testid="support-detail-status"><SelectValue /></SelectTrigger>
            <SelectContent className="glass">{Object.entries(SUPPORT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <WhatsAppButton number={req.whatsapp_number} testId="support-detail-whatsapp" />
        </div>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Note interne (facultatif)" data-testid="support-detail-note"
                  className="w-full resize-none rounded-xl border border-border bg-surface-input px-4 py-2.5 text-sm text-white focus:border-brand/60 focus:outline-none" />
        <p className="text-xs text-muted-foreground">Le bouton WhatsApp ouvre la conversation avec un message prérempli ; rien n'est envoyé sans ta validation dans WhatsApp. Une demande d'assistance n'attribue jamais de crédits.</p>
        <button onClick={save} disabled={busy} data-testid="support-detail-save" className="w-full rounded-full bg-brand py-2.5 font-semibold text-black btn-glow disabled:opacity-60">Enregistrer</button>
      </DialogContent>
    </Dialog>
  );
}

export function SupportRequestsTab() {
  const [status, setStatus] = useState('NEW');
  const [source, setSource] = useState('all');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { q: q || undefined, status: status === 'ALL' ? undefined : status, source: source === 'all' ? undefined : source };
    try { const { data } = await api.get('/admin/support-requests', { params }); setRows(data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [q, status, source]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-4" data-testid="admin-support">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (nom, numéro, identifiant, e-mail)" data-testid="support-search"
                 className="w-full rounded-xl border border-border bg-surface-input py-2.5 pl-9 pr-4 text-sm text-white placeholder:text-muted-foreground focus:border-brand/60 focus:outline-none" />
        </div>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-40 border-border bg-surface-input" data-testid="support-source-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="glass"><SelectItem value="all">Toutes sources</SelectItem><SelectItem value="android">Android</SelectItem><SelectItem value="web">Web</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="flex flex-wrap gap-2">
        {['NEW', 'IN_PROGRESS', 'RESOLVED', 'CLOSED', 'ALL'].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`support-filter-${s}`}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold ${status === s ? 'bg-brand text-black' : 'bg-surface-card text-white hover:bg-surface-cardHover'}`}>
            {SUPPORT_LABELS[s] || 'Toutes'}
          </button>
        ))}
      </div>
      {loading ? <LoadingState /> : rows.length === 0 ? (
        <EmptyState icon={LifeBuoy} title="Aucune demande" description="Aucune demande d'assistance pour ces filtres." />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-surface-card font-mono text-xs uppercase tracking-widest text-muted-foreground">
              <tr><th className="p-3">Date</th><th className="p-3">Nom</th><th className="p-3">WhatsApp</th><th className="p-3">Source</th><th className="p-3">Statut</th><th className="p-3" /></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => setOpen(r)} className="cursor-pointer border-t border-border hover:bg-white/5" data-testid={`support-row-${r.id}`}>
                  <td className="p-3 text-muted-foreground">{fmt(r.created_at)}</td>
                  <td className="p-3 text-white">{r.display_name}{r.username && <span className="text-xs text-muted-foreground"> @{r.username}</span>}</td>
                  <td className="p-3 font-mono text-white">{r.whatsapp_number}</td>
                  <td className="p-3 text-muted-foreground">{r.source}</td>
                  <td className="p-3"><Badge s={r.status} /></td>
                  <td className="p-3 text-right"><WhatsAppButton number={r.whatsapp_number} testId={`support-whatsapp-${r.id}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && <Detail req={open} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </div>
  );
}
